import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const AdminAuthContext = createContext(null);
const TOKEN_KEY = "csm_admin_token";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) : null;
    if (!stored) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/admin/auth/me`, { headers: { Authorization: `Bearer ${stored}` } })
      .then((res) => {
        setToken(stored);
        setAdmin(res.data);
      })
      .catch(() => {
        window.localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => setChecking(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/admin/auth/login`, { email, password });
    const { access_token, admin: a } = res.data;
    window.localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setAdmin(a);
    return a;
  }, []);

  const logout = useCallback(() => {
    window.localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAdmin(null);
  }, []);

  const authFetch = useCallback(
    (path, config = {}) => {
      const headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
      return axios({ url: `${API}${path}`, ...config, headers });
    },
    [token]
  );

  const value = useMemo(
    () => ({ token, admin, checking, login, logout, authFetch }),
    [token, admin, checking, login, logout, authFetch]
  );
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
